import { Badge, useTheme } from "@deere/fuel-react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StarIcon from "@mui/icons-material/Star";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DealerApi from "../../../../../api/dealer/DealerApi";
import { DealerAccount } from "../../../../../api/dealer/types";
import useDealerApi from "../../../../../api/dealer/useDealerApi";
// import OrderApi from "../../../../../api/orders/orderApi";
// import { SavedAddress } from "../../../../../api/orders/orderInterfaces";
// import useOrderApi from "../../../../../api/orders/useOrderApi";
import { ConfigContext } from "../../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../../context/DealerAccountContext";
import { RBACAccessContext } from "../../../../../context/RBACAccessContext/RBACAccessContext";
import ResourceLink from "../../../../orders/components/ResourceLink/ResourceLink";
import { D2DOrderRow, D2DStatus } from "../D2DActionMenu/D2DActionMenu";

interface D2DShipOrderModalProps {
  open: boolean;
  request: D2DOrderRow | null;
  onClose: () => void;
  onMarkShipped: (
    request: D2DOrderRow,
    carrierName: string,
    trackingNumber: string
  ) => void | Promise<void>;
}

const renderStatusBadge = (
  t: (key: string, defaultValue?: string) => string,
  status: D2DStatus,
  timeToRespond?: string
) => {
  switch (status) {
    case "NEW":
      return (
        <Badge
          startIcon={<StarIcon />}
          variant="info"
          data-testid="d2d-ship-status-badge"
        >
          {timeToRespond ??
            t("d2d:d2dRequests.status.new", "New request pending")}
        </Badge>
      );
    case "ACCEPTED":
      return (
        <Badge
          startIcon={<CheckCircleIcon />}
          variant="success"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.accepted", "Accepted")}
        </Badge>
      );
    case "REJECTED":
      return (
        <Badge
          startIcon={<WarningAmberIcon />}
          variant="error"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.rejected", "Rejected")}
        </Badge>
      );
    case "EXPIRED":
      return (
        <Badge
          startIcon={<WarningAmberIcon />}
          variant="warning"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.expired", "Expired")}
        </Badge>
      );
    case "SHIPPED":
      return (
        <Badge
          startIcon={<LocalShippingIcon />}
          variant="success"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.shipped", "Shipped")}
        </Badge>
      );
    case "COMPLETED":
      return (
        <Badge
          startIcon={<CheckCircleIcon />}
          variant="success"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.completed", "Completed")}
        </Badge>
      );
    case "CREDITED":
      return (
        <Badge
          startIcon={<CheckCircleIcon />}
          variant="success"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.credited", "Credited")}
        </Badge>
      );
    default:
      return (
        <Badge variant="info" data-testid="d2d-ship-status-badge">
          {status}
        </Badge>
      );
  }
};

const D2DShipOrderModal = ({
  open,
  request,
  onClose,
  onMarkShipped,
}: D2DShipOrderModalProps) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation(["d2d"]);
  const {
    DEALER_SERVICE_URL,
    // ORDERS_SERVICE_URL,
    ENABLE_RBAC_AUTH_SUPPORT,
    // ENABLE_RBAC_AUTH_SUPPORT_FOR_ORDER_SERVICE,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE,
  } = useContext(ConfigContext);
  const { authState } = useOktaAuth();
  const accessToken = authState?.accessToken?.accessToken as string;
  // const isOrderServiceApiAuthEnable =
  //   ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_ORDER_SERVICE;
  const isDealerServiceApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE;
  const { dealerAccount } = useContext(DealerAccountContext);
  const { isRBACEnabled } = useContext(RBACAccessContext);
  const dealerApi = useDealerApi();
  // const orderApi = useOrderApi();
  const [shipFromAccount, setShipFromAccount] = useState<DealerAccount | null>(
    null
  );
  // const [shipToAddress, setShipToAddress] = useState<SavedAddress | null>(null);
  const [shipToAccount, setShipToAccount] = useState<DealerAccount | null>(
    null
  );

  const [carrierName, setCarrierName] = useState<string>("");
  const [trackingNumber, setTrackingNumber] = useState<string>("");

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCarrierName("");
      setTrackingNumber("");
    }
  }, [open]);

  // Ship To is no longer read from the order service summary API.
  // useEffect(() => {
  //   const fetchOrderDetails = async () => {
  //     if (
  //       open &&
  //       request?.orderNumber &&
  //       (request.buyerAccountId || dealerAccount?.id) &&
  //       ORDERS_SERVICE_URL
  //     ) {
  //       try {
  //         const accountId = request.buyerAccountId ?? dealerAccount!.id;
  //         const order = isOrderServiceApiAuthEnable
  //           ? await orderApi.summary(
  //               ORDERS_SERVICE_URL,
  //               request.orderNumber,
  //               accountId,
  //               i18n.language
  //             )
  //           : await OrderApi.summary(
  //               ORDERS_SERVICE_URL,
  //               accessToken,
  //               request.orderNumber,
  //               accountId,
  //               i18n.language
  //             );
  //         if (order && order.shipToAddress) {
  //           setShipToAddress(order.shipToAddress as SavedAddress);
  //         }
  //       } catch (error) {
  //         console.error("Error fetching order details:", error);
  //       }
  //     }
  //   };
  //
  //   fetchOrderDetails();
  // }, [
  //   open,
  //   request?.orderNumber,
  //   request?.buyerAccountId,
  //   dealerAccount?.id,
  //   ORDERS_SERVICE_URL,
  //   i18n.language,
  // ]);

  // Ship To address is read from the dealer service using the buyer account
  useEffect(() => {
    const fetchShipToAddress = async () => {
      const buyerAccountId = request?.buyerAccountId;
      if (buyerAccountId && DEALER_SERVICE_URL) {
        try {
          const accounts = isDealerServiceApiAuthEnable
            ? await dealerApi.getAccountsDetails(
                DEALER_SERVICE_URL,
                [buyerAccountId],
                i18n.language
              )
            : await DealerApi.getAccountsDetails(
                DEALER_SERVICE_URL,
                [buyerAccountId],
                accessToken,
                i18n.language
              );
          if (accounts && accounts.length > 0) {
            setShipToAccount(accounts[0]);
          }
        } catch (error) {
          console.error("Error fetching ship to address:", error);
        }
      }
    };

    if (open) {
      fetchShipToAddress();
    }
  }, [open, request?.buyerAccountId, DEALER_SERVICE_URL, i18n.language]);

  useEffect(() => {
    const fetchShipFromAddress = async () => {
      if (dealerAccount?.id && DEALER_SERVICE_URL) {
        try {
          const accounts = isDealerServiceApiAuthEnable
            ? await dealerApi.getAccountsDetails(
                DEALER_SERVICE_URL,
                [dealerAccount.id],
                i18n.language
              )
            : await DealerApi.getAccountsDetails(
                DEALER_SERVICE_URL,
                [dealerAccount.id],
                accessToken,
                i18n.language
              );
          if (accounts && accounts.length > 0) {
            setShipFromAccount(accounts[0]);
          }
        } catch (error) {
          console.error("Error fetching ship from address:", error);
        }
      }
    };

    if (open) {
      fetchShipFromAddress();
    }
  }, [open, dealerAccount?.id, DEALER_SERVICE_URL, i18n.language]);

  if (!request) {
    return null;
  }

  const handleMarkShipped = async () => {
    try {
      await onMarkShipped(request, carrierName, trackingNumber);
    } catch (error) {
      console.error("Failed to ship D2D order", error);
    }
  };

  const closeLabel = t("d2d:d2dRequests.shipModal.close", "Close") ?? "Close";

  const shipFrom = {
    name: shipFromAccount?.name ?? "",
    addressLine1: shipFromAccount?.location?.addressLine1 ?? "",
    addressLine2: shipFromAccount?.location
      ? `${shipFromAccount.location.city}, ${shipFromAccount.location.state} ${shipFromAccount.location.postalCode}`
      : "",
    country: shipFromAccount?.location?.country ?? "",
  };
  // const shipTo = {
  //   name: shipToAddress?.name ?? "",
  //   addressLine1: shipToAddress?.addressLineOne ?? "",
  //   addressLine2: shipToAddress
  //     ? `${shipToAddress.cityName}, ${shipToAddress.stateOrProvinceName} ${shipToAddress.zipCode}`
  //     : "",
  //   country: shipToAddress?.countryCode ?? "",
  // };
  const shipTo = {
    name: shipToAccount?.name ?? "",
    addressLine1: shipToAccount?.location?.addressLine1 ?? "",
    addressLine2: shipToAccount?.location
      ? `${shipToAccount.location.city}, ${shipToAccount.location.state} ${shipToAccount.location.postalCode}`
      : "",
    country: shipToAccount?.location?.country ?? "",
  };

  const isShippedOrLater =
    request.status === "SHIPPED" ||
    request.status === "COMPLETED" ||
    request.status === "CREDITED";

  const isAccepted = request.status === "ACCEPTED";

  const darkBorderColor = "#000000";
  const shippingInputSx = {
    "& .MuiInputBase-root:not(.Mui-focused), & .MuiInputBase-root:not(.Mui-focused):hover":
      {
        boxShadow: `inset 0 0 0 2px ${darkBorderColor} !important`,
        "& .MuiOutlinedInput-notchedOutline, & fieldset": {
          borderColor: `${darkBorderColor} !important`,
          borderWidth: "2px !important",
        },
      },
  };

  const encodedPartNumber = encodeURIComponent(request.partNumber);
  // /parts/:partNumber is only routed when RBAC is on; otherwise use the dealer-scoped route
  const partInfoUrl = isRBACEnabled
    ? `/parts/${encodedPartNumber}`
    : `/dealer/${dealerAccount?.id}/parts/${encodedPartNumber}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="d2d-ship-order-title"
      fullWidth
      maxWidth="sm"
      data-testid="d2d-ship-order-modal"
    >
      <DialogTitle id="d2d-ship-order-title" data-testid="d2d-ship-modal-title">
        {t("d2d:d2dRequests.shipOrderTitle", {
          defaultValue: "View Order {{orderNumber}}",
          orderNumber: request.orderNumber,
        })}
      </DialogTitle>
      <IconButton
        aria-label={closeLabel}
        onClick={onClose}
        size="large"
        sx={{
          position: "absolute",
          right: 15,
          top: 15,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: theme.tokens.FuelSpacing10,
            rowGap: theme.tokens.FuelSpacing6,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                color: theme.tokens.FuelColorTextPrimary,
                mb: theme.tokens.FuelSpacing1,
              }}
            >
              {t(
                "d2d:d2dRequests.shipModal.dealerReference",
                "Dealer reference"
              )}
            </Typography>
            <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
              {request.dealerReference}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                color: theme.tokens.FuelColorTextPrimary,
                mb: theme.tokens.FuelSpacing1,
              }}
            >
              {t("d2d:d2dRequests.shipModal.orderStatus", "Order status")}
            </Typography>
            {renderStatusBadge(t, request.status, request.timeToRespond)}
          </Box>

          <Box sx={{ gridColumn: "1 / span 2" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr",
                columnGap: theme.tokens.FuelSpacing10,
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontWeight: theme.tokens.FuelFontWeightSemibold,
                    color: theme.tokens.FuelColorTextPrimary,
                    mb: theme.tokens.FuelSpacing1,
                  }}
                >
                  {t(
                    "d2d:d2dRequests.shipModal.partInformation",
                    "Part information"
                  )}
                </Typography>
                <Box data-testid="d2d-part-information">
                  <ResourceLink value={request.partNumber} url={partInfoUrl} />
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: theme.tokens.FuelFontWeightSemibold,
                    color: theme.tokens.FuelColorTextPrimary,
                    mb: theme.tokens.FuelSpacing1,
                  }}
                >
                  {t(
                    "d2d:d2dRequests.shipModal.requestedQuantity",
                    "Requested quantity"
                  )}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {request.requestedQuantity}
                </Typography>
              </Box>

              {(isAccepted || isShippedOrLater) && (
                <Box>
                  <Typography
                    sx={{
                      fontWeight: theme.tokens.FuelFontWeightSemibold,
                      color: theme.tokens.FuelColorTextPrimary,
                      mb: theme.tokens.FuelSpacing1,
                    }}
                  >
                    {t(
                      "d2d:d2dRequests.shipModal.acceptedQuantity",
                      "Accepted quantity"
                    )}
                  </Typography>
                  <Typography
                    sx={{ color: theme.tokens.FuelColorTextSecondary }}
                  >
                    {request.acceptedQuantity}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {(isAccepted || isShippedOrLater) && (
            <>
              <Box>
                <Typography
                  sx={{
                    fontWeight: theme.tokens.FuelFontWeightSemibold,
                    color: theme.tokens.FuelColorTextPrimary,
                    mb: theme.tokens.FuelSpacing1,
                  }}
                >
                  {t("d2d:d2dRequests.shipModal.shipFrom", "Ship from")}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {shipFrom.name}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {shipFrom.addressLine1}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {shipFrom.addressLine2}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {shipFrom.country}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: theme.tokens.FuelFontWeightSemibold,
                    color: theme.tokens.FuelColorTextPrimary,
                    mb: theme.tokens.FuelSpacing1,
                  }}
                >
                  {t("d2d:d2dRequests.shipModal.shipTo", "Ship to")}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {shipTo.name}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {shipTo.addressLine1}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {shipTo.addressLine2}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {shipTo.country}
                </Typography>
              </Box>

              {isShippedOrLater && (
                <Box
                  sx={{
                    gridColumn: "1 / span 2",
                    mt: theme.tokens.FuelSpacing4,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: theme.tokens.FuelFontWeightSemibold,
                      color: theme.tokens.FuelColorTextPrimary,
                      mb: theme.tokens.FuelSpacing2,
                    }}
                  >
                    {t(
                      "d2d:d2dRequests.shipModal.shippingInformation",
                      "Shipping information"
                    )}
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      columnGap: theme.tokens.FuelSpacing6,
                      rowGap: theme.tokens.FuelSpacing4,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: theme.tokens.FuelFontSizeBody,
                          color: theme.tokens.FuelColorTextTertiary,
                          mb: theme.tokens.FuelSpacing1,
                        }}
                      >
                        {t(
                          "d2d:d2dRequests.shipModal.carrierName",
                          "Carrier name"
                        )}
                      </Typography>
                      <Typography
                        sx={{ color: theme.tokens.FuelColorTextSecondary }}
                      >
                        {request.carrierName ?? "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: theme.tokens.FuelFontSizeBody,
                          color: theme.tokens.FuelColorTextTertiary,
                          mb: theme.tokens.FuelSpacing1,
                        }}
                      >
                        {t(
                          "d2d:d2dRequests.shipModal.trackingNumber",
                          "Tracking number"
                        )}
                      </Typography>
                      <Typography
                        sx={{ color: theme.tokens.FuelColorTextSecondary }}
                      >
                        {request.trackingNumber ?? "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}

          {isAccepted && (
            <Box
              sx={{ gridColumn: "1 / span 2", mt: theme.tokens.FuelSpacing4 }}
            >
              <Typography
                sx={{
                  fontWeight: theme.tokens.FuelFontWeightSemibold,
                  color: theme.tokens.FuelColorTextPrimary,
                  mb: theme.tokens.FuelSpacing2,
                }}
              >
                {t(
                  "d2d:d2dRequests.shipModal.shippingInformation",
                  "Shipping information"
                )}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  columnGap: theme.tokens.FuelSpacing6,
                  rowGap: theme.tokens.FuelSpacing4,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: theme.tokens.FuelFontWeightSemibold,
                      color: theme.tokens.FuelColorTextPrimary,
                      mb: theme.tokens.FuelSpacing1,
                    }}
                  >
                    {t("d2d:d2dRequests.shipModal.carrierName", "Carrier name")}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={carrierName}
                    onChange={(e) => setCarrierName(e.target.value)}
                    sx={shippingInputSx}
                    data-testid="d2d-ship-carrier-input"
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: theme.tokens.FuelFontWeightSemibold,
                      color: theme.tokens.FuelColorTextPrimary,
                      mb: theme.tokens.FuelSpacing1,
                    }}
                  >
                    {t(
                      "d2d:d2dRequests.shipModal.trackingNumber",
                      "Tracking number"
                    )}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    sx={shippingInputSx}
                    data-testid="d2d-ship-tracking-input"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="secondary"
          onClick={onClose}
          data-testid="d2d-ship-cancel"
        >
          {t("d2d:d2dRequests.shipModal.cancel", "Cancel")}
        </Button>

        {isAccepted && (
          <Button
            variant="primary"
            onClick={handleMarkShipped}
            data-testid="d2d-ship-confirm"
            disabled={!carrierName || !trackingNumber}
          >
            {t("d2d:d2dRequests.shipModal.markAsShipped", "Create shipment")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default D2DShipOrderModal;
